import React, { useContext, useState } from "react";
import { Attendance, AttendanceStatus } from "../../../domains/models/attendance/attendance";
import { IAttendanceTableView } from "../attendance.types";
import EmployeeOverviewPopover from "../../employee/overview/employee-overview-popover/employee-overview-popover";
import LoadingSkeleton from "../../common/loading-skeleton/loading-skeleton";
import { Attendance_Sort_Field } from "../../../constant/constant";
import EmployeeAvatar from "../../employee/avatar/employee-avatar";
import Locale from "../../../core/locale/locale";
import dayjs from "dayjs";
import { Tag } from "antd";
import Grid from "../../../common/table";
import DateRangeFilter from "../../common/date-range-filter/date-range-filter";
import EmployeeSelect from "../../common/employee-select/employee-select";
import PunchLogs from "./punch-logs";
import ReporteeSelect from "../../common/reportee-select/reportee-select";
import AttendanceSearchContext from "../AttendanceContext";
import { removeUnderScoreWithLowerCase } from "../../../utility/utility";


const filters = [
    {text: "Monday", value: "Monday"},
    {text: "Tuesday", value: "Tuesday"},
    {text: "Wednesday", value: "Wednesday"},
    {text: "Thursday", value: "Thursday"},
    {text: "Friday", value: "Friday"},
    {text: "Saturday", value: "Saturday"},
    {text: "Sunday", value: "Sunday"}
];

const statusFilter = Object.entries(AttendanceStatus).map(([key, value]) => (
    {text: value.toLowerCase(), value: key}
))

const AttendanceTableView: React.FC<IAttendanceTableView> = (props) => {

    const {statusValue, setAttendanceSearch} = useContext(AttendanceSearchContext);
    const [selectedRow, setSelectedRow] = useState<Attendance | null>(null);

    const columns = [
        ...(props.isActionVisible
                ? [{
                    title: "Employee Name",
                    dataIndex: "employeeName",
                    key: "employeeName",
                    render: (_: any, record: any) => {
                        return (
                            <div className="employee-name-wrapper" key={record.id}>
                                <EmployeeAvatar
                                    name={record.employeeName}
                                    thumbnailId={record.profilePhotoThumbnailId}
                                    size={30}
                                />
                                <EmployeeOverviewPopover
                                    employeeId={record.employeeId}
                                    uniqueKey={`${record.employeeId}-${record.id}`}
                                >
                                    <div
                                        className="employee-search-table-name"
                                    >
                                        {record.employeeName?.firstName}{" "}
                                        {record.employeeName?.middleName}{" "}
                                        {record.employeeName?.lastName}
                                    </div>
                                </EmployeeOverviewPopover>
                            </div>
                        );
                    },
                    sorter: true,
                    sortOrder:
                        props.sortField === Attendance_Sort_Field.firstName
                            ? props.sortDirection === "ASC"
                            ? "ascend"
                            : "descend"
                            : null,
                    sortField: Attendance_Sort_Field.firstName,
                }]
                : []
        ),

        {
            title: "Date",
            dataIndex: "date",
            sorter: true,
            sortField: Attendance_Sort_Field.date,
            key: "date",
            sortOrder:
                props.sortField === Attendance_Sort_Field.date
                    ? props.sortDirection === "ASC"
                    ? "ascend"
                    : "descend"
                    : null,
            render: (_: any, attendanceList: any) => attendanceList?.date ? dayjs(attendanceList?.date).format(Locale.displayDate) : "-"
        },
        {
            title: 'Day',
            dataIndex: "date",
            key: 'day',
            render: (date: string) => dayjs(date).format("dddd"),
            filters: filters,
            onFilter: (value: string, record: any) => {
                const day = dayjs(record.date).format("dddd");
                return day === value;
            }
        },
        {
            title: "In Time",
            dataIndex: "checkInTime",
            key: "checkInTime",
        },
        {
            title: "Out Time",
            dataIndex: "checkOutTime",
            key: "checkOutTime",
        },
        {
            title: "Status",
            dataIndex: "status",
            sorter: true,
            sortField: Attendance_Sort_Field.status,
            sortOrder:
                props.sortField === Attendance_Sort_Field.status
                    ? props.sortDirection === "ASC"
                    ? "ascend"
                    : "descend"
                    : null,
            key: "status",
            render: (status: string) => {
                const statusVal = AttendanceStatus[status as keyof typeof AttendanceStatus]
                return (
                    <Tag className={`attendance-status ${status.toLowerCase()}`}>{statusVal}</Tag>
                )
            },
            filters: [{text: 'All', value: ''}, ...statusFilter],
            filterSearch: true,
        },
        {
            title: "Working Hours",
            dataIndex: "workingHours",
            key: "workingHours",
        }
    ];


    function handleRowClick(record: any) {
        setSelectedRow(record);
    }

    return (
        <div className={'pn-attendance-table'} id={'pn-attendance-table'}>
            <div className={'pn-attendance-table__heading'}>
                <h4 className={'pn-attendance-table__title'}>
                    {props.title ?? 'Employee activity'}
                    <p
                        className={'pn-attendance-table__sub-title'}>
                        Showing{' '}
                        <span
                            className={'bold-text'}>{removeUnderScoreWithLowerCase(statusValue ? statusValue : 'All')}</span>{' '}
                        attendance records
                    </p>
                </h4>
                <div className={"pn-attendance-table__toolbar"}>
                    <div className={'pn-attendance-table__action-wrapper'}>
                        <label className={'pn-attendance-table__action-label'}>Date Range</label>
                        <DateRangeFilter
                            defaultSelection={"Today"}
                            onChange={props.handleDateChange}
                        />
                    </div>

                    {props.isActionVisible &&
                    <div className={'pn-attendance-table__action-wrapper'}>
                        <label className={'pn-attendance-table__action-label'}>Employee</label>
                        {props.supervisorId ?
                            (<ReporteeSelect
                                    includeAllSelectOption
                                    supervisorId={props.supervisorId}
                                    placeholder="Select employee"
                                    value={props.employeeId}
                                    onChange={props.handleEmployeeId}/>
                            )
                            :
                            (<EmployeeSelect
                                includeAllSelectOption
                                placeholder="Select employee"
                                value={props.employeeId}
                                onChange={props.handleEmployeeId}
                            />)}
                    </div>
                    }
                </div>
            </div>


            <LoadingSkeleton loading={props.loading}>
                <Grid
                    rowKey="key"
                    bordered={true}
                    dataSource={props.list}
                    columns={columns}
                    onRowClick={props.showPunchOnClick ? handleRowClick : undefined}
                    paginationButton={true}
                    pageNumber={props.pageNumber}
                    pageSize={props.pageSize}
                    onChange={(pagination: any, filters: any, sorter: any) => {
                        const status = filters.status?.[0] as string;
                        if (filters.status) {
                            setAttendanceSearch('STATUS', status);
                            return;
                        }
                        const backendField = sorter.column?.sortField ?? Attendance_Sort_Field.firstName;
                        if (!backendField) return;
                        let direction: "ASC" | "DESC" | undefined;

                        switch (sorter.order) {
                            case "ascend":
                                direction = "ASC";
                                break;
                            case "descend":
                                direction = "DESC";
                                break;
                            default:
                                if (backendField === Attendance_Sort_Field.firstName) {
                                    direction = sorter.order === 'ascend' ? 'DESC' : 'ASC';
                                } else {
                                    direction = undefined;
                                }

                        }


                        props.handleSortChange(backendField, direction);

                    }}
                    handlePrevPage={props.handlePrevPage}
                    handleNextPage={props.handleNextPage}
                />
            </LoadingSkeleton>
            <PunchLogs selectedRow={selectedRow} onClose={() => setSelectedRow(null)}/>
        </div>
    );
};

export default AttendanceTableView;
